import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Panel, inputCls } from "./ui";

const GROUPS = { dominio: "Dominio ed email", pagamenti: "Pagamenti", spedizioni: "Spedizioni e resi", privacy: "Privacy e cookie", legale: "Legale e fiscale", catalogo: "Catalogo", sicurezza: "Sicurezza" };
const ICON = { ok: <CheckCircle2 className="w-4 h-4 text-[#4d6b3c]" />, warn: <AlertTriangle className="w-4 h-4 text-[#b07a2a]" />, todo: <Circle className="w-4 h-4 text-[#8a2f22]" /> };

const AutoRow = ({ c }) => (
  <li data-testid={`prelaunch-auto-${c.key}`} data-status={c.status} className="flex gap-3 py-2.5">
    <span className="pt-0.5">{ICON[c.status]}</span>
    <span><b className="text-sm">{c.label}</b><span className="block text-xs text-[#242321]/65">{c.detail}</span></span>
  </li>
);

const ManualRow = ({ m, onSave }) => {
  const [note, setNote] = useState(m.note);
  return (
    <li data-testid={`prelaunch-item-${m.key}`} className="py-3 flex flex-col sm:flex-row gap-3">
      <label className="flex gap-3 flex-1 cursor-pointer">
        <input type="checkbox" data-testid={`prelaunch-check-${m.key}`} checked={m.done} onChange={(e) => onSave(m.key, e.target.checked, note)} className="mt-1 accent-[#39251D]" />
        <span><b className={`text-sm ${m.done ? "line-through text-[#242321]/50" : ""}`}>{m.label}</b><span className="block text-xs text-[#242321]/65">{m.how}</span></span>
      </label>
      <input data-testid={`prelaunch-note-${m.key}`} value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== m.note && onSave(m.key, m.done, note)} placeholder="Nota" className={`${inputCls} sm:w-64`} />
    </li>
  );
};

export default function AdminPrelaunch() {
  const { isOwner } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/prelaunch").then((r) => setD(r.data)), []);
  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);
  const save = async (key, done, note) => {
    try { await api.put(`/admin/prelaunch/${key}`, { done, note }); await load(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!isOwner) return <Navigate to="/admin" replace />;
  if (!d) return null;
  const pct = Math.round((d.done / d.total) * 100);
  return (
    <div data-testid="admin-prelaunch" className="space-y-6">
      <PageHead title="Verifica pre-lancio" subtitle="Controllo completo di cosa manca per aprire davvero il negozio. Il sito non viene pubblicato automaticamente." />
      <div className="bg-white/60 border border-[#D5B98C]/50 p-5" data-testid="prelaunch-progress">
        <div className="flex justify-between text-sm"><b>{d.ready ? "Tutto pronto per l'apertura" : "Non ancora pronto per l'apertura"}</b><span data-testid="prelaunch-count">{d.done} / {d.total}</span></div>
        <div className="mt-3 h-1.5 bg-[#E9DFCE]"><div className="h-full bg-[#39251D] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      <Panel title="Controlli automatici" testId="prelaunch-auto">
        <ul className="divide-y divide-[#D5B98C]/40">{d.auto.map((c) => <AutoRow key={c.key} c={c} />)}</ul>
      </Panel>
      {Object.entries(GROUPS).map(([g, title]) => (
        <Panel key={g} title={title} testId={`prelaunch-group-${g}`}>
          <ul className="divide-y divide-[#D5B98C]/40">{d.manual.filter((m) => m.group === g).map((m) => <ManualRow key={m.key} m={m} onSave={save} />)}</ul>
        </Panel>
      ))}
    </div>
  );
}
